/**
 * UnderstaffingSummary - Model-toggled understaffing view for facility detail
 *
 * Anticipated/Hybrid toggle controls a table with 4 columns:
 *   Total Acuity Gap, % Days Failing to Staff to Acuity,
 *   Projected Total Understaffing Without Credit, With Credit
 *
 * Clicking a year row expands the acuity bar chart for that year.
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Collapse,
  IconButton,
  Popover,
  Switch,
  FormControlLabel,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

export type AcuityModel = 'anticipated' | 'hybrid';

interface ProsecutionBaseline {
  ant_pct_acuity_staffed_total: number | null;
  ant_pct_acuity_staffed_rn: number | null;
  [key: string]: any;
}

interface UnderstaffingSummaryProps {
  facilityYears: any[];
  facilityYearsHybrid: any[];
  facility: any;
  state: string;
  stateContext?: any;
  stateAcuityByYear?: Record<number, any>;
  sweetwaterBaseline?: ProsecutionBaseline;
  njBaseline?: ProsecutionBaseline;
  model: AcuityModel;
  onModelChange: (model: AcuityModel) => void;
}

interface AcuityBarRow {
  label: string;
  facilityPct: number | null;
  statePct: number | null;
}

interface ModelYearRow {
  year: number;
  acuityGap: number | null;
  pctDaysFailing: number | null;
  costNoCredit: number;
  costWithCredit: number;
  // For expand panel
  acuityTotal: number | null;
  acuityRn: number | null;
  acuityLpn: number | null;
  acuityAide: number | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const formatMoney = (val: number | null | undefined): string => {
  if (val == null) return '-';
  if (val === 0) return '$0';
  const absVal = Math.abs(val);
  if (absVal >= 1000000) return `$${(absVal / 1000000).toFixed(1)}M`;
  if (absVal >= 1000) return `$${Math.round(absVal / 1000).toLocaleString()}K`;
  return `$${Math.round(absVal).toLocaleString()}`;
};

const fmtAcuityGap = (pctStaffed: number | null | undefined) => {
  if (pctStaffed == null) return <span style={{ color: '#bdbdbd' }}>-</span>;
  const gap = pctStaffed - 100;
  const color = gap < 0 ? '#d32f2f' : '#2e7d32';
  const sign = gap >= 0 ? '+' : '';
  return <span style={{ fontWeight: 700, color }}>{sign}{gap.toFixed(1)}%</span>;
};

const fmtPctDays = (val: number | null | undefined) => {
  if (val == null) return <span style={{ color: '#bdbdbd' }}>-</span>;
  const color = val > 50 ? '#d32f2f' : val > 25 ? '#ed6c02' : undefined;
  return <span style={{ fontWeight: 600, color }}>{val.toFixed(1)}%</span>;
};

const fmtAcuityPct = (val: number | null | undefined) => {
  if (val == null) return '-';
  const diff = val - 100;
  const color = diff < 0 ? '#d32f2f' : '#2e7d32';
  const sign = diff >= 0 ? '+' : '';
  return <span style={{ fontWeight: 700, color }}>{sign}{diff.toFixed(1)}%</span>;
};

const MAX_PCT = 200;

const COLORS: Record<string, string> = {
  anticipated: 'rgba(123, 31, 162, 0.85)',
  hybrid: 'rgba(21, 101, 192, 0.85)',
};

// ─────────────────────────────────────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────────────────────────────────────

function computeAcuity(years: any[], model: AcuityModel = 'anticipated'): { total: number | null; rn: number | null; lpn: number | null; aide: number | null } {
  const p = model === 'hybrid' ? 'hybrid' : 'ant';
  let pbjRn = 0, mdsRn = 0, pbjLpn = 0, mdsLpn = 0, pbjCna = 0, mdsAide = 0;
  for (const fy of years) {
    const m = fy.metadata || fy;
    pbjRn += m[`${p}_pbj_rn_hrs`] || 0;
    mdsRn += m[`${p}_expected_rn_hrs_hours`] || 0;
    pbjLpn += m[`${p}_pbj_lpn_hrs`] || 0;
    mdsLpn += m[`${p}_expected_lpn_hrs_hours`] || 0;
    pbjCna += m[`${p}_pbj_cna_hrs`] || 0;
    mdsAide += m[`${p}_expected_cna_hrs_hours`] || 0;
  }
  const totalPbj = pbjRn + pbjLpn + pbjCna;
  const totalMds = mdsRn + mdsLpn + mdsAide;
  return {
    total: totalMds > 0 ? Math.round((totalPbj / totalMds) * 1000) / 10 : null,
    rn: mdsRn > 0 ? Math.round((pbjRn / mdsRn) * 1000) / 10 : null,
    lpn: mdsLpn > 0 ? Math.round((pbjLpn / mdsLpn) * 1000) / 10 : null,
    aide: mdsAide > 0 ? Math.round((pbjCna / mdsAide) * 1000) / 10 : null,
  };
}

function buildModelYearRows(facilityYears: any[], model: AcuityModel = 'anticipated'): ModelYearRow[] {
  const p = model === 'hybrid' ? 'hybrid' : 'ant';
  // pct_days field naming differs: ant uses pct_ant_days_*, hybrid uses hybrid_pct_days_*
  const pctDaysKey = model === 'hybrid' ? 'hybrid_pct_days_any_below' : 'pct_ant_days_any_below';

  return facilityYears
    .map((fy: any) => {
      const m = fy.metadata || fy;
      const costWithCredit = Math.max(0, fy.mds_understaffing_cost ?? 0);
      const costNoCredit = Math.max(0, fy.mds_understaffing_cost_no_credit ?? 0);

      // Acuity gap
      const pbjTotal = (m[`${p}_pbj_rn_hrs`] || 0) + (m[`${p}_pbj_lpn_hrs`] || 0) + (m[`${p}_pbj_cna_hrs`] || 0);
      const mdsTotal = (m[`${p}_expected_rn_hrs_hours`] || 0) + (m[`${p}_expected_lpn_hrs_hours`] || 0) + (m[`${p}_expected_cna_hrs_hours`] || 0);
      const acuityGap = mdsTotal > 0 ? Math.round((pbjTotal / mdsTotal) * 1000) / 10 : null;

      // Per-role acuity
      const acuityRn = (m[`${p}_expected_rn_hrs_hours`] || 0) > 0 ? Math.round(((m[`${p}_pbj_rn_hrs`] || 0) / m[`${p}_expected_rn_hrs_hours`]) * 1000) / 10 : null;
      const acuityLpn = (m[`${p}_expected_lpn_hrs_hours`] || 0) > 0 ? Math.round(((m[`${p}_pbj_lpn_hrs`] || 0) / m[`${p}_expected_lpn_hrs_hours`]) * 1000) / 10 : null;
      const acuityAide = (m[`${p}_expected_cna_hrs_hours`] || 0) > 0 ? Math.round(((m[`${p}_pbj_cna_hrs`] || 0) / m[`${p}_expected_cna_hrs_hours`]) * 1000) / 10 : null;

      // % days failing acuity (any role)
      const pctDaysFailing = m[pctDaysKey] ?? null;

      return {
        year: fy.year,
        acuityGap,
        pctDaysFailing,
        costNoCredit,
        costWithCredit,
        acuityTotal: acuityGap,
        acuityRn,
        acuityLpn,
        acuityAide,
      };
    })
    .filter((r) => r.year != null)
    .sort((a, b) => a.year - b.year);
}

// ─────────────────────────────────────────────────────────────────────────────
// ACUITY BAR CHART (inline expand)
// ─────────────────────────────────────────────────────────────────────────────

const AcuityChart: React.FC<{ rows: AcuityBarRow[] }> = ({ rows }) => {
  return (
    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
      {/* Left: Bar chart */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 14, height: 10, backgroundColor: '#66bb6a', opacity: 0.8, borderRadius: 0.5 }} />
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Facility</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 14, height: 10, backgroundColor: '#e3f2fd', border: '1px dashed #90caf9', borderRadius: 0.5 }} />
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>State Avg</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderLeft: '2.5px solid #212121', opacity: 0.7 }} />
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>100%</Typography>
          </Box>
        </Box>
        {rows.map((row) => {
          const fac = row.facilityPct ?? 0;
          const st = row.statePct ?? 0;
          const facWidth = Math.min(fac, MAX_PCT) / MAX_PCT * 100;
          const stWidth = Math.min(st, MAX_PCT) / MAX_PCT * 100;
          const markerLeft = (100 / MAX_PCT) * 100;
          const isBelow = fac < 100;
          return (
            <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, width: 40, color: 'text.secondary', flexShrink: 0 }}>
                {row.label}
              </Typography>
              <Box sx={{ flex: 1, position: 'relative', height: 24 }}>
                <Box sx={{
                  position: 'absolute', left: 0, top: 3, bottom: 3,
                  width: `${stWidth}%`, backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px dashed #90caf9',
                }} />
                <Box sx={{
                  position: 'absolute', left: 0, top: 0, bottom: 0,
                  width: `${facWidth}%`, backgroundColor: isBelow ? '#ef5350' : '#66bb6a',
                  opacity: 0.8, borderRadius: 1, transition: 'width 0.3s ease',
                }} />
                <Tooltip title="100% = facility staffed exactly to the acuity-adjusted benchmark for this staff category" arrow placement="top">
                  <Box sx={{
                    position: 'absolute', left: `${markerLeft}%`, top: -3, bottom: -3,
                    width: 2.5, backgroundColor: '#212121', opacity: 0.7, zIndex: 2,
                    cursor: 'help', ml: '-1px', px: '3px', mx: '-3px',
                  }} />
                </Tooltip>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Right: Table */}
      <Box sx={{ flexShrink: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', minWidth: 260 }}>
        <Box sx={{ display: 'flex', backgroundColor: '#f5f5f5', borderBottom: '2px solid', borderColor: 'divider' }}>
          <Box sx={{ width: 60, px: 1.5, py: 0.5, borderRight: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', textAlign: 'right' }}>&nbsp;</Typography>
          </Box>
          <Box sx={{ width: 90, px: 1.5, py: 0.5, textAlign: 'right', borderRight: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>Facility</Typography>
          </Box>
          <Box sx={{ width: 90, px: 1.5, py: 0.5, textAlign: 'right' }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#1976d2', textTransform: 'uppercase' }}>State</Typography>
          </Box>
        </Box>
        {rows.map((row, idx) => {
          const isLast = idx === rows.length - 1;
          return (
            <Box key={row.label} sx={{ display: 'flex', borderBottom: isLast ? 'none' : '1px solid', borderColor: 'divider' }}>
              <Box sx={{ width: 60, px: 1.5, py: 0.75, borderRight: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.secondary' }}>{row.label}</Typography>
              </Box>
              <Box sx={{ width: 90, px: 1.5, py: 0.75, textAlign: 'right', borderRight: '1px solid', borderColor: 'divider' }}>
                {fmtAcuityPct(row.facilityPct)}
              </Box>
              <Box sx={{ width: 90, px: 1.5, py: 0.75, textAlign: 'right' }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#1976d2' }}>
                  {row.statePct != null ? `${(row.statePct - 100) >= 0 ? '+' : ''}${(row.statePct - 100).toFixed(1)}%` : '-'}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const UnderstaffingSummary: React.FC<UnderstaffingSummaryProps> = ({
  facilityYears,
  facilityYearsHybrid,
  facility,
  state,
  stateContext,
  stateAcuityByYear,
  sweetwaterBaseline,
  njBaseline,
  model,
  onModelChange,
}) => {
  const [expandedYear, setExpandedYear] = useState<number | 'total' | null>(null);
  const [stateMinAnchor, setStateMinAnchor] = useState<HTMLElement | null>(null);
  const [showBenchmarks, setShowBenchmarks] = useState(false);

  // Case benchmark acuity gaps (deficit from 100%)
  const swAcuityGap = (sweetwaterBaseline?.ant_pct_acuity_staffed_total ?? 82.6) - 100;
  const njAcuityGap = (njBaseline?.ant_pct_acuity_staffed_total ?? 77.0) - 100;
  const meta = facility?.metadata || {};

  const yearRange = `${meta.first_year ?? '2020'}–${meta.last_year ?? '2025'}`;

  // Build rows for the selected model
  const anticipatedRows = useMemo(() => buildModelYearRows(facilityYears, 'anticipated'), [facilityYears]);
  const hybridRows = useMemo(() => buildModelYearRows(facilityYearsHybrid, 'hybrid'), [facilityYearsHybrid]);
  const rows = model === 'anticipated' ? anticipatedRows : hybridRows;

  // Totals
  const totalCostNoCredit = rows.reduce((s, r) => s + r.costNoCredit, 0);
  const totalCostWithCredit = rows.reduce((s, r) => s + r.costWithCredit, 0);

  // Overall acuity from all years
  const overallAcuity = useMemo(
    () => computeAcuity(model === 'hybrid' ? facilityYearsHybrid : facilityYears, model),
    [model, facilityYears, facilityYearsHybrid],
  );

  // Overall % days failing
  const overallDaysVals = rows.map((r) => r.pctDaysFailing).filter((v): v is number => v != null);
  const overallPctDaysFailing = overallDaysVals.length > 0 ? overallDaysVals.reduce((a, b) => a + b, 0) / overallDaysVals.length : null;

  // State acuity for bar chart
  const stateAcuity = useMemo(() => {
    if (!stateContext) return null;
    if (model === 'hybrid') {
      return {
        total: stateContext.hybrid_acuity_total ?? null,
        rn: stateContext.hybrid_acuity_rn ?? null,
        lpn: stateContext.hybrid_acuity_lpn ?? null,
        aide: stateContext.hybrid_acuity_aide ?? null,
      };
    }
    return {
      total: stateContext.avg_acuity_gap ?? null,
      rn: stateContext.avg_acuity_rn ?? null,
      lpn: stateContext.avg_acuity_lpn ?? null,
      aide: stateContext.avg_acuity_aide ?? null,
    };
  }, [model, stateContext]);

  // Build bar chart rows for a given year row (or overall)
  const buildBarRows = (acuity: { total: number | null; rn: number | null; lpn: number | null; aide: number | null }, year?: number): AcuityBarRow[] => {
    // Use year-specific state acuity when available, fall back to all-years
    let sa = stateAcuity;
    if (year != null && stateAcuityByYear?.[year]) {
      const yd = stateAcuityByYear[year];
      if (model === 'hybrid') {
        sa = {
          total: yd.hybrid_acuity_total ?? null,
          rn: yd.hybrid_acuity_rn ?? null,
          lpn: yd.hybrid_acuity_lpn ?? null,
          aide: yd.hybrid_acuity_aide ?? null,
        };
      } else {
        sa = {
          total: yd.avg_acuity_gap ?? null,
          rn: yd.avg_acuity_rn ?? null,
          lpn: yd.avg_acuity_lpn ?? null,
          aide: yd.avg_acuity_aide ?? null,
        };
      }
    }
    return [
      { label: 'RN', facilityPct: acuity.rn, statePct: sa?.rn ?? null },
      { label: 'LPN', facilityPct: acuity.lpn, statePct: sa?.lpn ?? null },
      { label: 'CNA', facilityPct: acuity.aide, statePct: sa?.aide ?? null },
    ];
  };

  // Static anticipated totals (always from anticipated model)
  const anticipatedAcuity = useMemo(() => computeAcuity(facilityYears, 'anticipated'), [facilityYears]);
  const anticipatedDaysVals = anticipatedRows.map((r) => r.pctDaysFailing).filter((v): v is number => v != null);
  const anticipatedPctDaysFailing = anticipatedDaysVals.length > 0 ? anticipatedDaysVals.reduce((a, b) => a + b, 0) / anticipatedDaysVals.length : null;
  const anticipatedTotalCost = anticipatedRows.reduce((s, r) => s + r.costNoCredit, 0);

  // Static hybrid total cost (with credit, floored to 0)
  const hybridTotalCost = hybridRows.reduce((s, r) => s + r.costWithCredit, 0);

  if (facilityYears.length === 0) {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight={700} mb={2}>Understaffing</Typography>
        <Typography color="text.secondary">No understaffing data available.</Typography>
      </Box>
    );
  }

  const accentColor = COLORS[model];
  const modelLabel = model === 'hybrid' ? 'Hybrid' : 'Anticipated';
  const handleRowClick = (year: number | 'total') => {
    setExpandedYear(expandedYear === year ? null : year);
  };

  return (
    <Box sx={{ mb: 4 }}>
      {/* Header */}
      <Typography variant="body2" color="text.secondary" mb={2}>
        Based on CMS PBJ staffing and MDS acuity data, {yearRange}
      </Typography>

      {/* Summary cards (static — always anticipated for acuity, separate for costs) */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {/* Total Acuity Gap (Anticipated) card dropped
        <Box sx={{ flex: 1, textAlign: 'center', p: 2, backgroundColor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem', lineHeight: 1.3, minHeight: 28 }}>
            Total Acuity Gap<br />(Anticipated) {yearRange}
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
            {fmtAcuityGap(anticipatedAcuity.total)}
          </Typography>
        </Box>
        */}
        <Box sx={{ flex: 1, textAlign: 'center', p: 2, backgroundColor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem', lineHeight: 1.3, minHeight: 28 }}>
            % Days Failing to Staff to Acuity<br />({modelLabel}) {yearRange}
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
            {fmtPctDays(overallPctDaysFailing)}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, textAlign: 'center', p: 2, backgroundColor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem', lineHeight: 1.3, minHeight: 28 }}>
            Projected Total Understaffing<br />({modelLabel}, No Credit) {yearRange}
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
            {formatMoney(totalCostNoCredit)}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, textAlign: 'center', p: 2, backgroundColor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem', lineHeight: 1.3, minHeight: 28 }}>
            Projected Total Understaffing<br />({modelLabel}, w/ Credit) {yearRange}
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
            {formatMoney(totalCostWithCredit)}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, textAlign: 'center', p: 2, backgroundColor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider', position: 'relative' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem', lineHeight: 1.3, minHeight: 28 }}>
              % Days Meeting {state}<br />State Staffing Requirement
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => setStateMinAnchor(e.currentTarget)}
              sx={{ p: 0, mt: -0.5 }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            </IconButton>
          </Box>
          <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
            {meta.pct_days_below_cms_hprd != null
              ? (() => {
                  const pctMet = 100 - meta.pct_days_below_cms_hprd;
                  return <span style={{ fontWeight: 700 }}>{pctMet.toFixed(1)}%</span>;
                })()
              : <span style={{ color: '#bdbdbd' }}>-</span>
            }
          </Typography>
          <Popover
            open={Boolean(stateMinAnchor)}
            anchorEl={stateMinAnchor}
            onClose={() => setStateMinAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Box sx={{ p: 2, minWidth: 320 }}>
              <Typography variant="subtitle2" fontWeight={700} mb={1}>
                State Minimum Compliance by Year
              </Typography>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                    <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 700 }}>Year</th>
                    <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 700 }}>Days Below Min</th>
                    <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 700 }}>% Days Met</th>
                  </tr>
                </thead>
                <tbody>
                  {facilityYears
                    .filter((fy: any) => fy.year != null)
                    .sort((a: any, b: any) => a.year - b.year)
                    .map((fy: any) => {
                      const m = fy.metadata || fy;
                      const daysBelow = m.total_days_below_cms_hprd ?? m.days_below_cms_hprd;
                      const pctShort = m.pct_days_below_cms_hprd;
                      const pctMet = pctShort != null ? 100 - pctShort : null;
                      const color = pctMet != null ? (pctMet < 50 ? '#d32f2f' : pctMet < 75 ? '#ed6c02' : undefined) : undefined;
                      return (
                        <tr key={fy.year} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '4px 8px', fontWeight: 600 }}>{fy.year}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                            {daysBelow != null ? Math.round(daysBelow) : '-'}
                          </td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600, color }}>
                            {pctMet != null ? `${pctMet.toFixed(1)}%` : '-'}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </Box>
          </Popover>
        </Box>
      </Box>

      {/* Model toggle + benchmark toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          Year-by-Year Breakdown
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Case Benchmarks toggle hidden — only drove the (now-dropped) Total Acuity Gap column
          <FormControlLabel
            control={
              <Switch
                checked={showBenchmarks}
                onChange={(e) => setShowBenchmarks(e.target.checked)}
                size="small"
              />
            }
            label={<Typography variant="body2" color="text.secondary">Case Benchmarks</Typography>}
          />
          */}
          <ToggleButtonGroup
            value={model}
            exclusive
            onChange={(_, v) => { if (v) { onModelChange(v); setExpandedYear(null); } }}
            sx={{
              '& .MuiToggleButton-root': {
                py: 0.75, px: 2.5, fontSize: '0.85rem', fontWeight: 600, textTransform: 'none',
                '&.Mui-selected': {
                  backgroundColor: accentColor, color: 'white',
                  '&:hover': { backgroundColor: accentColor },
                },
              },
            }}
          >
            <ToggleButton value="hybrid">Hybrid</ToggleButton>
            <ToggleButton value="anticipated">Anticipated</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Table */}
      <Box sx={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, width: 50 }}></th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700 }}>Year</th>
              {/* Total Acuity Gap column dropped
              <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700 }}>
                <Tooltip title="Total acuity staffing deficit (actual vs expected). Negative = understaffed" arrow>
                  <span>Total Acuity Gap</span>
                </Tooltip>
              </th>
              */}
              <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700 }}>
                <Tooltip title="Average percentage of days the facility fell below acuity benchmark across staffing groups" arrow>
                  <span>% Days Failing to Staff<br />to Acuity</span>
                </Tooltip>
              </th>
              <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700 }}>
                <Tooltip title="Understaffing cost with NO credit for overstaffed categories. Only counts understaffed hours." arrow>
                  <span>Projected Total Understaffing<br />Without Credit</span>
                </Tooltip>
              </th>
              <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700 }}>
                <Tooltip title="Understaffing cost WITH credit for overstaffed categories (overstaffing in one role offsets understaffing in another)." arrow>
                  <span>Projected Total Understaffing<br />With Credit</span>
                </Tooltip>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isExpanded = expandedYear === row.year;
              return (
                <React.Fragment key={row.year}>
                  <tr
                    onClick={() => handleRowClick(row.year)}
                    style={{
                      borderBottom: isExpanded ? 'none' : '1px solid #f0f0f0',
                      cursor: 'pointer',
                      backgroundColor: isExpanded ? '#fafbfc' : undefined,
                    }}
                  >
                    <td style={{ padding: '8px 4px 8px 12px', width: 30 }}>
                      <IconButton size="small" sx={{ p: 0, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                        <ExpandMoreIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </td>
                    <td style={{ padding: '8px 12px', fontWeight: 600 }}>{row.year}</td>
                    {/* Total Acuity Gap column dropped
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
                        {fmtAcuityGap(row.acuityGap)}
                        {showBenchmarks && row.acuityGap != null && (
                          <Box sx={{ ml: 1.5, textAlign: 'left' }}>
                            {(() => {
                              const gap = row.acuityGap - 100;
                              const swDiff = gap - swAcuityGap;
                              const njDiff = gap - njAcuityGap;
                              return (
                                <>
                                  <Tooltip title={`vs Sweetwater (${swAcuityGap.toFixed(1)}%)`} arrow>
                                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, lineHeight: 1.4, color: swDiff <= 0 ? 'error.main' : 'success.main', cursor: 'help' }}>
                                      Sweetwater {swDiff >= 0 ? '+' : ''}{swDiff.toFixed(1)}%
                                    </Typography>
                                  </Tooltip>
                                  <Tooltip title={`vs Deptford (${njAcuityGap.toFixed(1)}%)`} arrow>
                                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, lineHeight: 1.4, color: njDiff <= 0 ? 'error.main' : 'success.main', cursor: 'help' }}>
                                      Deptford {njDiff >= 0 ? '+' : ''}{njDiff.toFixed(1)}%
                                    </Typography>
                                  </Tooltip>
                                </>
                              );
                            })()}
                          </Box>
                        )}
                      </Box>
                    </td>
                    */}
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{fmtPctDays(row.pctDaysFailing)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: accentColor }}>{formatMoney(row.costNoCredit)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: accentColor }}>{formatMoney(row.costWithCredit)}</td>
                  </tr>
                  {/* Expand panel with acuity bar chart */}
                  <tr>
                    <td colSpan={5} style={{ padding: 0 }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{
                          px: 3, py: 2,
                          backgroundColor: '#fafbfc',
                          borderBottom: '2px solid',
                          borderColor: 'divider',
                          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.04)',
                        }}>
                          <Typography variant="body2" fontWeight={700} sx={{ mb: 1.5, fontSize: '0.85rem' }}>
                            {row.year} Staffing as % of Acuity by Staffing Group
                          </Typography>
                          <AcuityChart
                            rows={buildBarRows({
                              total: row.acuityTotal,
                              rn: row.acuityRn,
                              lpn: row.acuityLpn,
                              aide: row.acuityAide,
                            }, row.year)}
                          />
                        </Box>
                      </Collapse>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr
              onClick={() => handleRowClick('total')}
              style={{
                borderTop: '2px solid #e0e0e0',
                cursor: 'pointer',
                backgroundColor: expandedYear === 'total' ? '#fafbfc' : undefined,
              }}
            >
              <td style={{ padding: '8px 4px 8px 12px', width: 30 }}>
                <IconButton size="small" sx={{ p: 0, transform: expandedYear === 'total' ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                  <ExpandMoreIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </td>
              <td style={{ padding: '8px 12px', fontWeight: 700 }}>Total</td>
              {/* Total Acuity Gap column dropped
              <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
                  {fmtAcuityGap(overallAcuity.total)}
                  {showBenchmarks && overallAcuity.total != null && (
                    <Box sx={{ ml: 1.5, textAlign: 'left' }}>
                      {(() => {
                        const gap = overallAcuity.total - 100;
                        const swDiff = gap - swAcuityGap;
                        const njDiff = gap - njAcuityGap;
                        return (
                          <>
                            <Tooltip title={`vs Sweetwater (${swAcuityGap.toFixed(1)}%)`} arrow>
                              <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, lineHeight: 1.4, color: swDiff <= 0 ? 'error.main' : 'success.main', cursor: 'help' }}>
                                Sweetwater {swDiff >= 0 ? '+' : ''}{swDiff.toFixed(1)}%
                              </Typography>
                            </Tooltip>
                            <Tooltip title={`vs Deptford (${njAcuityGap.toFixed(1)}%)`} arrow>
                              <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, lineHeight: 1.4, color: njDiff <= 0 ? 'error.main' : 'success.main', cursor: 'help' }}>
                                Deptford {njDiff >= 0 ? '+' : ''}{njDiff.toFixed(1)}%
                              </Typography>
                            </Tooltip>
                          </>
                        );
                      })()}
                    </Box>
                  )}
                </Box>
              </td>
              */}
              <td style={{ padding: '8px 12px', textAlign: 'right' }}>{fmtPctDays(overallPctDaysFailing)}</td>
              <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: accentColor }}>{formatMoney(totalCostNoCredit)}</td>
              <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: accentColor }}>{formatMoney(totalCostWithCredit)}</td>
            </tr>
            <tr>
              <td colSpan={5} style={{ padding: 0 }}>
                <Collapse in={expandedYear === 'total'} timeout="auto" unmountOnExit>
                  <Box sx={{
                    px: 3, py: 2,
                    backgroundColor: '#fafbfc',
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.04)',
                  }}>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 1.5, fontSize: '0.85rem' }}>
                      All Years Staffing as % of Acuity by Staffing Group
                    </Typography>
                    <AcuityChart
                      rows={buildBarRows(overallAcuity)}
                    />
                  </Box>
                </Collapse>
              </td>
            </tr>
          </tfoot>
        </table>
      </Box>
    </Box>
  );
};

export default UnderstaffingSummary;
